<template>
	<div class="App" :class="classes" :data-version="version">
		<PrintosHeader v-if="$config.isHpMode" :$config="$config" />
		<div class="AppContainer"><router-view v-if="canRenderRoute" /></div>
		<RunFlowModal />
		<RunningFlowModal />
		<EditFlowInfoModal />
		<ConfirmModal />
		<Toast />
		<UpgradeAccountModal />
	</div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { set } from 'vue-analytics';
import RunFlowModal from './containers/RunFlow/RunFlowModal';
import RunningFlowModal from './containers/RunFlow/RunningFlowModal';
import EditFlowInfoModal from './containers/Flow/edit/EditFlowInfoModal';
import ConfirmModal from './components/ConfirmModal';
import Toast from './components/Toast';
import PrintosHeader from './components/PrintosHeader';
import UpgradeAccountModal from './components/UpgradeAccountModal';
import store2 from './lib/store2';

const publicRoutes = ['/login', '/preview'];

export default {
	components: {
		RunFlowModal,
		RunningFlowModal,
		ConfirmModal,
		Toast,
		PrintosHeader,
		EditFlowInfoModal,
		UpgradeAccountModal
	},
	data: () => ({
		version: process.env.VERSION,
		tempToken: null,
		hpidTempTokenProcessed: false,
		tokenRefreshInitialized: false,
		isAuthBootstrapping: true
	}),
	computed: {
		...mapGetters(['$config', 'user', 'canRunAutoflow']),
		...mapGetters('auth', ['isAuthenticated', 'token']),
		classes() {
			return [
				{
					'is-hpMode': this.$config.isHpMode
				},
				`Route-${this.$route.name}`
			];
		},
		queryAccountId() {
			return _.get(this.$route, 'query.accountId');
		},
		queryToken() {
			return _.get(this.$route, 'query.token');
		},
		canRenderRoute() {
			if (publicRoutes.includes(this.$route.path)) return true;
			return !this.isAuthBootstrapping;
		}
	},
	watch: {
		isAuthenticated: {
			immediate: true,
			async handler() {
				await this.redirectIfUnauthenticated();

				if (
					this.isAuthenticated &&
					!this.$config.isHpMode &&
					!this.tokenRefreshInitialized &&
					!this.isAuthBootstrapping
				) {
					this.tokenRefreshInitialized = true;
					this.setupHourlyTokenRefresh();
				}
			}
		},
		'user._id': {
			immediate: true,
			handler(userId) {
				if (this.$ga && userId) {
					this.$ga.set('userId', userId);
				}
			}
		},
		currentAccount() {
			if (this.currentAccount) {
				set('dimension1', this.currentAccount._id);
				set('dimension2', this.currentAccount.name);
			}
		},
		canRunAutoflow: {
			immediate: true,
			handler(val) {
				if (!this.$config.isHpMode || typeof val !== 'boolean') return;

				if (!val) this.setUpgradeAccountModal({ isOpen: true, allowClose: false, requiredPlan: true });
			}
		}
	},
	async created() {
		this.tempToken = this.getHpIdTempToken();
		try {
			if (this.$config.isHpMode && this.isAuthenticated) {
				await this.fetchBaseData();
				return;
			}

			// None HP Mode:
			// if the token and accountId are set in the query string (in non-hp mode), let's use them
			if (this.queryAccountId && this.queryToken) {
				await this.setAuthFromQueryString({ accountId: this.queryAccountId, token: this.queryToken });

				// once used, remove token and accountId from the query
				const newRoute = _.omit(_.merge({}, this.$route), ['query.accountId', 'query.token']);
				this.$router.replace(newRoute);

				// This callback is for non-HP auth exchange and should not be processed as HPID temp token.
				this.tempToken = null;
				this.hpidTempTokenProcessed = true;
			}

			if (
				!this.$config.isHpMode &&
				this.isAuthenticated &&
				!this.tempToken &&
				!(this.queryAccountId && this.queryToken)
			) {
				await this.fetchBaseData();

				if (!this.tokenRefreshInitialized) {
					this.tokenRefreshInitialized = true;
					this.setupHourlyTokenRefresh();
				}
			}

			// HPID Login Mode:
			if (this.tempToken && !this.hpidTempTokenProcessed) {
				try {
					await this.$store.dispatch('auth/loginHpId', this.tempToken);

					// remove the token from the query string to prevent it from being used again
					if (this.queryToken) {
						const newRoute = _.omit(_.merge({}, this.$route), ['query.token']);
						await this.$router.replace(newRoute);
					}

					await this.fetchBaseData();

					// Setup token refresh if not in HP mode (for HPID login in non-HP environments)
					if (!this.$config.isHpMode && !this.tokenRefreshInitialized) {
						this.tokenRefreshInitialized = true;
						this.setupHourlyTokenRefresh();
					}
				} finally {
					this.hpidTempTokenProcessed = true;
					this.tempToken = null;
				}
			}
		} catch (err) {
			this.displayToast({
				data: {
					type: 'error',
					title: this.$t('Error'),
					body: this.$t('An error occurred while checking the temporary token')
				}
			});
		} finally {
			this.isAuthBootstrapping = false;
			await this.redirectIfUnauthenticated();
		}
	},
	methods: {
		...mapActions(['fetchBaseData', 'setAuthFromQueryString', 'displayToast']),
		...mapActions('auth', ['setupHourlyTokenRefresh']),
		...mapActions('upgradeAccountModal', { setUpgradeAccountModal: 'setOptions' }),
		getHpIdTempToken() {
			const queryToken = _.get(this, '$route.query.token');
			if (queryToken) return queryToken;

			const match = window.location.href.match(/[?#&]token=([^&#]+)/);
			return match && match[1] ? decodeURIComponent(match[1]) : null;
		},
		async redirectIfUnauthenticated() {
			const isPublicRoute = publicRoutes.includes(this.$route.path);
			if (this.isAuthBootstrapping) {
				return;
			}

			if (!this.isAuthenticated && !isPublicRoute) {
				if (!this.$config.isHpMode) {
					await this.$router.push({ name: 'login', query: { redirectTo: this.$route.path } });
					return;
				}

				const currentUrl = window.location.href;
				const printosBase = this.$config.printosBase;
				const isDebugMode = store2.get('debugMode');
				if (isDebugMode) {
					// eslint-disable-next-line
					console.info('HP mode: not authenticated.');
				} else {
					window.location.href = `${printosBase}/start/#/signin?target=${currentUrl}`;
				}
			}
		}
	}
};
</script>

<style lang="scss">
@import './style/_index.scss';

.App {
	display: flex;
	flex-direction: column;
	height: 100vh;
	margin: 0;
}

.OneflowAppLayout {
	flex: 1;
	height: 100%;
}

.AppContainer {
	flex: 1;
	overflow: auto;
	position: relative;
	z-index: 100; // printos topbar has z-index 50
}

.MainModalContainer {
	position: relative;
	z-index: 1000000;
}
.tippy-tooltip .tippy-content {
	font-size: 14px;
}
</style>
